import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft, IconAlertTriangle, IconCheck,
  IconUser, IconClock, IconMapPin, IconTractor,
  IconPhone, IconDownload, IconShieldCheck,
} from '@tabler/icons-react';

export default function EmergencyDetail() {
  const { language } = useTranslation();
  const { emergencyId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, role } = useAuth();
  const [emergency, setEmergency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !emergencyId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId, 'emergencies', emergencyId));
        if (snap.exists()) setEmergency({ id: snap.id, ...snap.data() });
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId, emergencyId]);

  const handleResolve = async () => {
    setResolving(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'emergencies', emergencyId), {
        status: 'resolved',
        resolvedAt: serverTimestamp(),
        resolvedBy: userProfile?.name,
        updatedAt: serverTimestamp(),
      });
      setEmergency(prev => ({ ...prev, status: 'resolved', resolvedBy: userProfile?.name }));
      toast.success('Emergency marked as resolved');
    } catch (err) { toast.error('Failed to resolve emergency'); }
    setResolving(false);
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const triggerLabel = (type) => {
    if (type === 'impact') return 'Impact detected';
    if (type === 'manual') return 'Manual SOS triggered';
    if (type === 'man_down') return 'Man-down alert';
    if (type === 'no_movement') return 'No movement detected';
    return type || '—';
  };

  const getElapsed = (start, end) => {
    if (!start || !end) return '—';
    const s = start.toDate ? start.toDate() : new Date(start);
    const e = end.toDate ? end.toDate() : new Date(end);
    const secs = Math.floor((e - s) / 1000);
    if (secs < 60) return secs + 's';
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    return mins + 'm ' + rem + 's';
  };

  if (loading) return <Layout><div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div></Layout>;

  if (!emergency) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/emergencies')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Emergency log
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Emergency not found</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>

        <div onClick={() => navigate('/emergencies')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Emergency log
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 20, fontWeight: 400 }}>Emergency event</h1>
              <StatusPill status={emergency.status || 'resolved'} size="lg" />
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {triggerLabel(emergency.triggerType)} · {formatDateTime(emergency.triggeredAt)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" icon={<IconDownload size={13} stroke={1.5} />} onClick={() => toast.info('HSE export coming soon')}>Export HSE</Button>
            {emergency.status === 'active' && (role === 'org_admin' || role === 'manager') && (
              <Button variant="success" size="sm" icon={<IconShieldCheck size={13} stroke={1.5} />} onClick={handleResolve} disabled={resolving} loading={resolving}>Mark resolved</Button>
            )}
          </div>
        </div>

        {emergency.status === 'active' && (
          <div style={{ background: 'var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <IconAlertTriangle size={18} color="#fff" stroke={1.5} />
            <div style={{ fontSize: 13, color: '#fff' }}>This emergency is still marked as active</div>
          </div>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Timeline</div>
          {[
            { label: 'Emergency triggered', time: formatDateTime(emergency.triggeredAt), colour: 'var(--color-danger)', icon: IconAlertTriangle },
            emergency.call999FiredAt && { label: '999 called automatically', time: formatDateTime(emergency.call999FiredAt), colour: 'var(--color-danger)', icon: IconPhone },
            emergency.status === 'resolved' && { label: 'Resolved by ' + (emergency.resolvedBy || '—'), time: formatDateTime(emergency.resolvedAt), colour: 'var(--color-success)', icon: IconCheck },
          ].filter(Boolean).map((event, i, arr) => {
            const Icon = event.icon;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: i < arr.length - 1 ? 14 : 0 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: event.colour + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={14} color={event.colour} stroke={1.5} />
                </div>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{event.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{event.time}</div>
                </div>
              </div>
            );
          })}
          {emergency.status === 'resolved' && emergency.triggeredAt && emergency.resolvedAt && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '0.5px solid var(--color-border-light)', fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              Total duration: {getElapsed(emergency.triggeredAt, emergency.resolvedAt)}
            </div>
          )}
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Incident details</div>
          {[
            { icon: IconUser,          label: 'Operator',     value: emergency.triggeredByName || '—' },
            { icon: IconAlertTriangle, label: 'Trigger',      value: triggerLabel(emergency.triggerType) },
            { icon: IconTractor,       label: 'Machine',      value: emergency.machineName || '—' },
            { icon: IconAlertTriangle, label: 'Impact force', value: emergency.impactForce ? emergency.impactForce + 'G' : '—' },
            { icon: IconMapPin,        label: 'What3Words',   value: emergency.location?.what3words ? '///' + emergency.location.what3words : '—' },
            { icon: IconMapPin,        label: 'Coordinates',  value: emergency.location?.lat ? emergency.location.lat.toFixed(6) + ', ' + emergency.location.lng.toFixed(6) : '—' },
          ].map(row => {
            const Icon = row.icon;
            return (
              <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <Icon size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', width: 110, flexShrink: 0 }}>{row.label}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-primary)', fontFamily: row.label === 'Coordinates' ? 'var(--font-mono)' : 'inherit' }}>{row.value}</span>
              </div>
            );
          })}
        </Card>

        {emergency.call999FiredAt && (
          <Card style={{ marginBottom: 16, border: '0.5px solid var(--color-danger)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-danger-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconPhone size={18} color="var(--color-danger)" stroke={1.5} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-danger)', fontWeight: 400 }}>Emergency services called automatically</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{formatDateTime(emergency.call999FiredAt)}</div>
              </div>
            </div>
          </Card>
        )}

        {emergency.responders?.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Responders ({emergency.responders.length})</div>
            {emergency.responders.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < emergency.responders.length - 1 ? '0.5px solid var(--color-border-light)' : 'none' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--color-success-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconCheck size={14} color="var(--color-success)" stroke={2} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{r.name}</div>
                  {r.distanceMiles && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 1 }}>{r.distanceMiles} miles from incident</div>}
                </div>
                {r.respondedAt && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{new Date(r.respondedAt).toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}</div>}
              </div>
            ))}
          </Card>
        )}

        {emergency.location?.lat && (
          <Card>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10 }}>Location</div>
            {emergency.location.what3words && (
              <div style={{ fontSize: 16, fontFamily: 'var(--font-mono)', color: 'var(--color-text-primary)', marginBottom: 8 }}>
                {'///' + emergency.location.what3words}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>
              {emergency.location.lat.toFixed(6) + ', ' + emergency.location.lng.toFixed(6)}
            </div>
            <div
              onClick={() => window.open('https://www.google.com/maps?q=' + emergency.location.lat + ',' + emergency.location.lng, '_blank')}
              style={{ fontSize: 13, color: 'var(--color-brand)', display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
            >
              <IconMapPin size={14} stroke={1.5} />
              Open in Google Maps
            </div>
          </Card>
        )}

      </div>
    </Layout>
  );
}
